import { Button, Flex, IconButton, Text } from '@chakra-ui/react'
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'
import { DARK_TABLE_BORDER, DARK_TEXT_MUTED, DARK_TEXT_PRIMARY, DARK_TEXT_SUBTLE } from 'constants/dark-tokens'
import type { DarkPaginationProps } from './types'

// Page-number list with simple truncation: always show first + last, and a
// window of pages around the current one, collapsing the rest into ellipses.
function buildPages(current: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
  const pages: (number | 'ellipsis')[] = [1]
  const start = Math.max(2, current - 1)
  const end = Math.min(totalPages - 1, current + 1)
  if (start > 2) pages.push('ellipsis')
  for (let p = start; p <= end; p += 1) pages.push(p)
  if (end < totalPages - 1) pages.push('ellipsis')
  pages.push(totalPages)
  return pages
}

const DarkPagination = ({ page, pageSize, total, onPageChange }: DarkPaginationProps) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  if (totalPages <= 1) return null

  const go = (next: number) => {
    const clamped = Math.min(Math.max(1, next), totalPages)
    if (clamped !== page) onPageChange(clamped)
  }

  const pageBtnSx = (active: boolean) => ({
    minW: '32px',
    h: '32px',
    px: '8px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: active ? 600 : 500,
    color: active ? DARK_TEXT_PRIMARY : DARK_TEXT_MUTED,
    bg: active ? 'rgba(46, 143, 204, 0.18)' : 'transparent',
    border: `1px solid ${active ? 'rgba(77, 179, 255, 0.45)' : 'transparent'}`,
    _hover: { bg: 'rgba(255, 255, 255, 0.06)' }
  })

  // Prev/next arrows. The disabled state must read clearly dimmer than the muted
  // enabled color — Chakra's ghost default only drops opacity, leaving the
  // near-white icon looking active on the dark surface. Pin an explicit subtle
  // color and suppress the hover highlight while disabled.
  const navBtnSx = {
    color: DARK_TEXT_MUTED,
    bg: 'transparent',
    _hover: { bg: 'rgba(255, 255, 255, 0.06)' },
    _disabled: {
      color: DARK_TEXT_SUBTLE,
      opacity: 0.4,
      cursor: 'not-allowed',
      _hover: { bg: 'transparent' }
    }
  }

  return (
    <Flex
      align="center"
      justify="space-between"
      px="16px"
      py="0"
      h="64px"
      boxSizing="border-box"
      borderTop={`1px solid ${DARK_TABLE_BORDER}`}
      gap="12px"
      flexWrap="nowrap"
    >
      <Text fontSize="13px" color={DARK_TEXT_MUTED} m={0}>
        {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
      </Text>

      <Flex align="center" gap="4px">
        <IconButton
          aria-label="Previous page"
          icon={<ChevronLeftIcon boxSize="18px" />}
          size="sm"
          variant="ghost"
          isDisabled={page <= 1}
          onClick={() => go(page - 1)}
          sx={navBtnSx}
        />
        {buildPages(page, totalPages).map((p, i) =>
          p === 'ellipsis' ? (
            <Text key={`e-${i}`} color={DARK_TEXT_MUTED} px="4px" m={0}>
              …
            </Text>
          ) : (
            <Button key={p} onClick={() => go(p)} sx={pageBtnSx(p === page)}>
              {p}
            </Button>
          )
        )}
        <IconButton
          aria-label="Next page"
          icon={<ChevronRightIcon boxSize="18px" />}
          size="sm"
          variant="ghost"
          isDisabled={page >= totalPages}
          onClick={() => go(page + 1)}
          sx={navBtnSx}
        />
      </Flex>
    </Flex>
  )
}

export default DarkPagination
